// SCREEN S5 — THE GUIDED CUT (spec 07 §4.5, slice 07-C-3). Floor PWA.
//
// The operator is standing at the saw with a failed drum. This screen shows the
// plan the SERVER computed from the fault position they typed on §4.4's
// execution screen, lets them move the cut marks outward to a convenient mark on
// the tape, and runs §3.5d's lifecycle: propose → confirm → execute, or cancel.
//
// ── THE FIVE THINGS IT MUST NOT GET WRONG ──────────────────────────────────
//
// 1. IT NEVER QUEUES (D-166, the largest shape decision in 07-C). §4.5 as
//    originally written called this screen "offline-capable (cut queues; labels
//    print on sync)"; that clause is STRUCK. A guided cut consumes a physical
//    unit, mints two, posts a waste ledger row, creates two inspections and
//    closes a third, and there is no un-cutting a cable — there is no
//    commutative projection a queue replay could reconcile. §9.3's own
//    `QUALITY_CUT_LENGTH_CHANGED` 409 exists BECAUSE the source length can go
//    stale between propose and execute, which is exactly the staleness a queued
//    cut cannot detect. So offline every control stays on screen, DEAD, with its
//    reason inside the slab (D-118's floor-button pattern), and this screen adds
//    NO operation kind to the floor outbox.
//
// 2. IT RE-DERIVES NOTHING ABOUT ELIGIBILITY. Whether a cut may be planned here
//    is `guided_cut.eligible` on the inspection read and nothing else, and when
//    it cannot the sentence comes from `guidedCutReasonKey` — the §4.4 sheet's
//    OWN key function over the sheet's own numbers — so the control that sent
//    the operator here and the page they land on cannot disagree about which
//    trim, which minimum or which drum. D-181 exists because two
//    implementations of that judgement drifted on the SERVER; a third one here,
//    offline, would be worse.
//
// 3. LENGTHS ARE EXACT DECIMALS, ALWAYS (D-041/D-042/D-156). Every length on
//    this screen is `numeric(18,6)` text. It is compared through scaled bigints
//    (`cut-geometry.ts`) and rendered through `formatMeasure`. `Number()` appears
//    exactly once, inside `positionPct`, to place a block on the cable diagram —
//    a pixel is not a verdict, and nothing reads that number back.
//
// 4. THE CLIENT'S ARITHMETIC IS A COURTESY, NEVER THE AUTHORITY (§4.5). It
//    performs ONE honest check — do these segments still tile the length this
//    snapshot shows — and names the snapshot it came from. The server re-tiles
//    every adjustment and re-validates the whole tiling at execute (§9.3); this
//    screen's outward-only guard exists so the operator is told AT the handle
//    rather than by a 422 after a round trip. A COURTESY IS NOT A LICENCE TO BE
//    STRICTER: `windowProblem` runs the server's own three tests, in the
//    server's order, against the server's own numbers — a client that refuses
//    what `retileGuidedCut` would take is not protecting anyone, it is telling
//    an operator a legal cut is illegal with no way to argue.
//
// 5. EVERY REFUSAL IS RELAYED WITH ITS OWN SENTENCE. `QUALITY_CUT_LOT_HELD`
//    names the lot quarantine, `QUALITY_CUT_SUBJECT_HELD` names the other
//    inspection's hold and its author, `QUALITY_CUT_SUBJECT_QUARANTINED` names
//    module 06's own release door, `QUALITY_CUT_NOT_ELIGIBLE` names the scrap
//    door, `QUALITY_CUT_NOT_CONFIRMED` names the race — these are DECISIONS with
//    continuations in them. The one refusal with a control of its own is
//    `QUALITY_CUT_LENGTH_CHANGED`: its `current_length_m` extension is the fresh
//    measurement, and the honest continuation is to RE-PLAN, which is one call
//    (a new propose reads module 06's current measured length and supersedes the
//    stale cut — D-162). §4.5's older wording, "operator re-measures and edits
//    source length first", describes an editable source length that does not
//    exist on any wire: `POST /inspections/{id}/cut-events` carries no source
//    unit and no source length AT ALL (D-155 pt 8), deliberately, so there is
//    nothing here to edit and a re-plan is the whole of the recompute. The
//    re-measure itself is module 06's act and is offered on §4.4's sheet.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { client } from "../../../lib/api";
import { unwrap } from "../../../lib/request";
import { formatMeasure, parseMeasure } from "../../office/inventory/lib/measure";
import {
  guidedCutReasonKey,
  guidedCutReasonValues,
} from "../../office/quality/run/guided-cut-action";
import { guidedCutOf } from "../../office/quality/hooks/quality-queries";
import type {
  QualityCutEventDetail,
  QualityCutExecuteResponse,
  QualityCutSegment,
  QualityInspectionDetail,
} from "../../office/quality/hooks/quality-queries";
import {
  FloorButton,
  FloorEmpty,
  FloorError,
  FloorScreen,
  FloorSkeleton,
} from "../components/floor-chrome";
import { QtyStepper } from "../components/qty-stepper";
import { floorReadSignal, useCachedRead } from "../hooks/use-cached-read";
import type { GuidedCutArm } from "../hooks/use-guided-cut";
import { useGuidedCutArm } from "../hooks/use-guided-cut";
import {
  demotedSegments,
  measureToText,
  positionPct,
  segmentsTile,
  windowProblem,
} from "../lib/cut-geometry";
import { MEASURE_MAX_PRECISION } from "../lib/qty-entry";
import { localeText } from "../../office/quality/run/inspection-run";
import { useFloorQualityGrants } from "./floor-quality";
import "./floor-cut.css";

/** What the last cut call came to. Held by the screen so the refs survive the
 * inspection re-read that follows an applied one. */
interface CutRefusal {
  readonly code: string | undefined;
  readonly message: string;
  readonly detail: string | undefined;
  readonly requestId: string | undefined;
}

/** Every cut call answers this shape (`use-guided-cut.ts`): a value, never a
 * throw, because these refusals are decisions with continuations in them. */
type CutOutcome = Awaited<ReturnType<GuidedCutArm["confirm"]>>;

export function FloorGuidedCutScreen() {
  const { t, i18n } = useTranslation();
  const { id } = useParams({ from: "/floor/quality/$id/cut" });
  const grants = useFloorQualityGrants();
  const arm = useGuidedCutArm();

  // The SAME cached snapshot the execution screen holds (one cache key, one
  // read): a device that opened the check while online can render this screen's
  // header and its eligibility verdict without the network, which is what makes
  // "a cut is taken online" a sentence about the WRITE rather than a blank page.
  const detail = useCachedRead<QualityInspectionDetail>({
    queryKey: ["floor", "quality-inspection", id],
    cacheKey: `quality-inspection:${id}`,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/quality/inspections/{id}", {
          params: { path: { id } },
          signal: floorReadSignal(signal),
        }),
        "quality-inspection",
      ),
  });

  const block = guidedCutOf(detail.data);
  const cutEventId = block?.cut_event_id ?? null;

  const [plan, setPlan] = useState<QualityCutEventDetail | undefined>(undefined);
  const [refusal, setRefusal] = useState<CutRefusal | undefined>(undefined);
  const [executed, setExecuted] = useState<QualityCutExecuteResponse | undefined>(undefined);
  const [armed, setArmed] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [loadingPlan, setLoadingPlan] = useState(false);

  // Load the LIVE cut the inspection points at (D-162's discovery mechanism:
  // §5 has no list route for cut events on purpose — the inspection is the
  // thing the operator has in their hand). One load per id, guarded by a ref so
  // a re-render cannot re-issue it.
  const loadedFor = useRef<string | null>(null);
  useEffect(() => {
    if (cutEventId === null || !arm.online) return;
    if (loadedFor.current === cutEventId) return;
    loadedFor.current = cutEventId;
    setLoadingPlan(true);
    void (async () => {
      const outcome = await arm.read(cutEventId);
      setLoadingPlan(false);
      if (outcome.kind === "done") setPlan(outcome.result);
      else setRefusal(outcome);
    })();
  }, [cutEventId, arm]);

  const event = plan?.cut_event;
  const segments = useMemo(
    () => [...(plan?.segments ?? [])].sort((a, b) => a.seq - b.seq),
    [plan?.segments],
  );
  const waste = segments.find((s) => s.kind === "waste");

  // ── THE TWO HANDLE POSITIONS, in the wire's own decimal text ──────────────
  //
  // Seeded from the waste segment each time a NEW plan lands, keyed by the
  // plan's id AND its update instant together (a re-tile keeps the id and
  // moves the marks), and the key is held BESIDE the values so the three can
  // never disagree about which plan they describe.
  //
  // SEEDED DURING RENDER, NOT IN AN EFFECT, and that is load-bearing rather
  // than clever (React's own "adjusting state when a prop changes" — a
  // render-phase update to THIS component is discarded and re-run before
  // anything is committed or painted). An effect seeds one commit LATE, which
  // leaves a frame where the handles are empty because nobody has filled them
  // yet — and since an empty handle now reads as `unreadable` (below), that
  // frame would paint a red alert, and `role="alert"` would ANNOUNCE it, on
  // every single plan load. Guarding the read with a "have we seeded" flag
  // papers over the ambiguity; seeding before the first paint deletes it, and
  // "empty" then means exactly one thing: the operator cleared it.
  const [handles, setHandles] = useState({ key: "", from: "", to: "" });
  const planKey = event === undefined ? "" : `${event.id}:${event.updated_at}`;
  if (planKey.length > 0 && handles.key !== planKey) {
    setHandles({ key: planKey, from: waste?.from_m ?? "", to: waste?.to_m ?? "" });
  }
  const handleFrom = handles.from;
  const handleTo = handles.to;
  const setHandleFrom = (next: string): void => {
    setHandles((h) => ({ ...h, from: next }));
  };
  const setHandleTo = (next: string): void => {
    setHandles((h) => ({ ...h, to: next }));
  };

  /** The handles' DISPLAY, and only their display: the same `formatMeasure`
   * the segment list, the snapshot line and the source length all use. The
   * stepper keeps the exact value it was handed (`qty-stepper.tsx`'s
   * `formatDisplay` contract) — this function never touches what is sent. */
  const handleDisplay = useCallback(
    (text: string) => formatMeasure(text, { locale: i18n.language }),
    [i18n.language],
  );

  // HAS THE OPERATOR MOVED A MARK? Compared through `parseMeasure`, so
  // "49.5" and "49.500000" are the same mark and neither is an adjustment.
  //
  // AN EMPTIED HANDLE IS AN ADJUSTMENT TO AN UNREADABLE MARK (2026-08-21
  // review), and that is the whole of the third fix. This used to demand both
  // handles be NON-EMPTY, so clearing one — select-all, backspace, which is
  // two gestures at a saw — left a blank field, NO problem sentence, the
  // confirm still live, and a cut confirmed at the ORIGINAL window while the
  // glass showed a blank mark. No data was ever at risk (the server re-tiles
  // from its own stored window), but "I checked the number" and "I thought I
  // had" are not the same thing in front of a running saw. `parseMeasure("")`
  // is `undefined`, which differs from any real mark, so a blank handle now
  // falls through to `windowProblem` and comes back "unreadable" — the same
  // sentence garbage input gets, because it is the same fact.
  const adjusted =
    waste !== undefined &&
    (parseMeasure(handleFrom) !== parseMeasure(waste.from_m) ||
      parseMeasure(handleTo) !== parseMeasure(waste.to_m));

  const handleProblem =
    event === undefined || waste === undefined || !adjusted
      ? undefined
      : windowProblem({
          sourceLengthM: event.source_length_m,
          faultPositionM: event.fault_position_m,
          trimAllowanceM: event.trim_allowance_m,
          fromM: handleFrom,
          toM: handleTo,
          // THE SERVER'S OWN TOPOLOGY TEST, taken against the same number
          // `retileGuidedCut` takes it against: how many segments this plan
          // has right now. A proxy ("does the window touch an end") refused
          // legal adjustments on every already-clamped plan — see
          // `windowProblem`.
          currentSegmentCount: segments.length,
        });

  // §4.5's ONE honest check, named with the snapshot it was taken against.
  const tiles = event === undefined ? true : segmentsTile(segments, event.source_length_m);

  // WHICH pieces are being DEMOTED — a length of real cable going to scrap for
  // being under the minimum — and never "is there any waste at all", which is
  // true of every ordinary cut because the TRIM is waste (`demotedSegments`
  // carries the argument and the standing wire request).
  const demoted = useMemo(
    () =>
      event === undefined
        ? []
        : demotedSegments(
            segments,
            {
              sourceLengthM: event.source_length_m,
              faultPositionM: event.fault_position_m,
              trimAllowanceM: event.trim_allowance_m,
            },
            block?.min_sellable_length_m,
          ),
    [segments, event, block?.min_sellable_length_m],
  );

  /** One settlement path for propose / confirm / cancel: the server's row wins,
   * the server's refusal is relayed whole, and either way the inspection is
   * re-read — an applied propose stamps `cut_event_id`, a cancel clears it, and
   * a REFUSAL means the server holds a truth this screen does not (the §4.4
   * sheet's R1 rule, applied to the same class of surface). */
  const run = async (call: () => Promise<CutOutcome>): Promise<void> => {
    const outcome = await call();
    if (outcome.kind === "done") {
      setRefusal(undefined);
      setPlan(outcome.result);
      loadedFor.current = outcome.result.cut_event.id;
    } else {
      setRefusal(outcome);
    }
    detail.refetch();
  };

  /** A cut the operator can still act on. `cancelled` and `completed` are
   * terminal (§2.8's lifecycle), and a terminal row on screen is precisely when
   * a NEW plan is the next thing to offer. */
  const planIsLive = event?.status === "proposed" || event?.status === "confirmed";

  // §9.3's re-measure race, and the ONE refusal on this surface with a control
  // of its own. The server's sentence carries the fresh `current_length_m`; the
  // continuation is a re-plan, which needs no number from the client because a
  // new propose reads module 06's current measured length itself and supersedes
  // this cut in its own transaction (D-162).
  const lengthChanged = refusal?.code === "QUALITY_CUT_LENGTH_CHANGED";

  const back = (
    <Link to="/floor/quality/$id" params={{ id }} className="floor-back" data-testid="cut-back">
      {t("quality.cut.back")}
    </Link>
  );

  // ---- the gates, in order: authority, then connectivity, then eligibility --

  if (grants.isResolved && !grants.isFeedError && !grants.canCut) {
    return (
      <FloorScreen title={t("quality.cut.title")} back={back}>
        <FloorEmpty title={t("quality.cut.refusedTitle")} body={t("quality.cut.refusedBody")} />
      </FloorScreen>
    );
  }

  // THE VERDICT IS THE SERVER'S, and it is the same field the §4.4 sheet's
  // fifth control renders from. What this screen does NOT reuse is the sheet's
  // render CLASS: absent-versus-dead is a rule about a CONTROL among controls
  // (D-118), and a page has nothing to hide — a person who reached this URL is
  // owed a sentence whichever of the nine reasons is true. So: `eligible`
  // decides whether a cut can be planned, and `guidedCutReasonKey` — the sheet's
  // own key function over the sheet's own numbers — decides what is said when
  // it cannot.
  const eligible = block?.eligible === true;

  return (
    <FloorScreen title={t("quality.cut.title")} back={back}>
      {/* ONLINE-ONLY, said once, at the top, where it explains every dead
          control below it (D-166 / D-118). Not an error: the device is fine and
          the plan on screen is real — what cannot happen offline is the WRITE. */}
      {!arm.online ? (
        <p className="floor-cut-offline" role="status" data-testid="cut-offline">
          {t("quality.cut.onlineOnly")}
        </p>
      ) : null}

      {detail.isLoading && detail.data === undefined ? <FloorSkeleton rows={5} /> : null}

      {detail.data === undefined && !detail.isLoading ? (
        <FloorError
          title={t("quality.cut.loadErrorTitle")}
          detail={t("quality.cut.loadErrorBody")}
          onRetry={detail.refetch}
        />
      ) : null}

      {detail.data !== undefined ? (
        <section className="floor-panel" data-testid="cut-subject">
          {/* The guided-cut panel names the check the operator is cutting
              against — a floor screen, so the pinned version's BUNDLE (#128),
              resolved by the same `localeText` the queue and the run use. */}
          <h2 className="floor-panel-head">
            {localeText(
              detail.data.inspection.control_point_name_i18n,
              i18n.language,
              detail.data.inspection.control_point_code,
            )}
          </h2>
          <p className="floor-card-sub">{detail.data.inspection.inspection_no}</p>
          {block !== undefined && block.source_length_m !== null ? (
            <p className="floor-card-measure" data-testid="cut-source-length">
              {t("quality.cut.sourceLength", {
                length: formatMeasure(block.source_length_m, { locale: i18n.language }),
              })}
            </p>
          ) : null}
          {block !== undefined && block.genealogy_depth !== null ? (
            <p className="floor-card-meta" data-testid="cut-depth">
              {t("quality.cut.depth", { depth: block.genealogy_depth })}
            </p>
          ) : null}
        </section>
      ) : null}

      {/* NOT ELIGIBLE — the SAME sentence the sheet's dead control carries,
          from the same block and the same key function. Two surfaces, one
          verdict; a second wording here would eventually quote a different trim
          than the control that sent the operator over.

          The screen says something for ALL NINE reasons, including the two the
          SHEET renders as an absence: a person who reached this URL — a
          bookmark, a stale tab, a scan that resolved to the wrong drum — is
          owed a sentence, and "there is no cut on this check" is exactly the
          fact the sheet expresses by having no button at all. */}
      {detail.data !== undefined && !eligible && !planIsLive && executed === undefined ? (
        <FloorEmpty
          title={t("quality.cut.notAvailableTitle")}
          body={t(
            guidedCutReasonKey(block?.reason ?? undefined, block),
            guidedCutReasonValues(block, i18n.language),
          )}
        />
      ) : null}

      {loadingPlan ? <FloorSkeleton rows={3} /> : null}

      {/* THE REFUSAL, in the server's own words, with its request id. */}
      {refusal !== undefined ? (
        <div className="floor-state floor-state-error" role="alert" data-testid="cut-refused">
          <p className="floor-state-title">{refusal.message}</p>
          {refusal.detail !== undefined && refusal.detail !== refusal.message ? (
            <p className="floor-state-body">{refusal.detail}</p>
          ) : null}
          {refusal.requestId !== undefined ? (
            <p className="floor-state-rid">
              {t("inventory.common.requestId")}: <code>{refusal.requestId}</code>
            </p>
          ) : null}
          {lengthChanged ? (
            <p className="floor-state-body" data-testid="cut-length-changed">
              {t("quality.cut.lengthChangedHint")}
            </p>
          ) : null}
        </div>
      ) : null}

      {/* NO LIVE PLAN — the propose. §3.5d step 1 calls this the operator's tap
          on "Guided cut"; the tap lands HERE rather than on the sheet so its
          refusals have the surface that can re-plan.

          "No LIVE plan" and not "no plan": a CANCELLED cut is a row this screen
          still renders (its reason is the record of why somebody dropped a plan
          staged over a customer's cable), and gating the propose on
          `plan === undefined` stranded the operator on that message with no way
          back — a dead end they could only leave by navigating away and
          returning. `cancelled` and `completed` are terminal, so a terminal row
          on screen is exactly when a new plan is the next thing to offer. */}
      {detail.data !== undefined && eligible && !planIsLive && !loadingPlan ? (
        <section className="floor-panel" data-testid="cut-unplanned">
          <p className="floor-card-sub">{t("quality.cut.notPlannedBody")}</p>
          <FloorButton
            variant="primary"
            testId="cut-plan"
            disabled={!arm.online || arm.busy}
            disabledReason={!arm.online ? t("quality.cut.onlineOnly") : undefined}
            onClick={() => {
              void run(() => arm.propose(id));
            }}
          >
            {t("quality.cut.plan")}
          </FloorButton>
        </section>
      ) : null}

      {event !== undefined && executed === undefined ? (
        <>
          <CutDiagram
            segments={segments}
            sourceLengthM={event.source_length_m}
            faultPositionM={event.fault_position_m}
          />

          <section className="floor-panel" data-testid="cut-segments">
            <h2 className="floor-panel-head">{t("quality.cut.planHead")}</h2>
            <p className="floor-card-meta" data-testid="cut-snapshot">
              {t("quality.cut.snapshot", {
                length: formatMeasure(event.source_length_m, { locale: i18n.language }),
              })}
            </p>
            <ul className="floor-cut-segments">
              {segments.map((segment) => (
                <li
                  key={segment.id}
                  className={
                    segment.kind === "stock" ? "floor-cut-seg" : "floor-cut-seg floor-cut-seg-waste"
                  }
                  data-testid={`cut-segment-${segment.kind}`}
                >
                  <span className="floor-cut-seg-name">
                    {t(`quality.cut.segment.${segment.kind}`, {
                      from: formatMeasure(segment.from_m, { locale: i18n.language }),
                      to: formatMeasure(segment.to_m, { locale: i18n.language }),
                    })}
                  </span>
                  {/* The LENGTH gets its own testid because the name beside it
                      already quotes two lengths — "Keep — 0 m to 49.5 m" — so a
                      walkthrough asserting "the segment is 49.5 m" against the
                      row would be satisfied by the RANGE and never read this
                      number at all. Added 2026-08-21 (07-C-4) after M07-S02 had
                      to reach for the CSS class to say what it meant. */}
                  <span className="floor-cut-seg-len" data-testid="cut-segment-length">
                    {t("quality.cut.segmentLength", {
                      length: formatMeasure(segment.length_m, { locale: i18n.language }),
                    })}
                  </span>
                </li>
              ))}
            </ul>
            {/* AMBER, QUOTING THE MINIMUM (§4.5's checklist). A demoted piece is
                the plan telling the operator that a length of real cable is
                going to scrap, and the number it was measured against is the
                only way they can argue with it.

                IT FIRES ON A DEMOTION AND ON NOTHING ELSE. The predicate here
                was `segments.some(kind === "waste")` until 2026-08-21, which
                is TRUE OF EVERY CUT — the trim is a waste segment, and the trim
                is the fault being removed rather than a piece anyone scrapped
                for being short. So an ordinary 49.5 / 1.0 / 49.5 plan told the
                operator a good piece had gone to scrap, they looked, found no
                short piece, and learned to ignore the strip. `demotedSegments`
                separates the two the way the server's own tiling does. */}
            {demoted.length > 0 ? (
              <p className="floor-cut-demoted" role="status" data-testid="cut-below-minimum">
                {t("quality.cut.belowMinimum", {
                  minimum: formatMeasure(block?.min_sellable_length_m, { locale: i18n.language }),
                })}
              </p>
            ) : null}
            {!tiles ? (
              <p className="floor-cut-broken" role="alert" data-testid="cut-tiling-broken">
                {t("quality.cut.tilingBroken", {
                  length: formatMeasure(event.source_length_m, { locale: i18n.language }),
                })}
              </p>
            ) : null}
          </section>

          {event.status === "proposed" ? (
            <section className="floor-panel" data-testid="cut-handles">
              <h2 className="floor-panel-head">{t("quality.cut.handlesHead")}</h2>
              {waste === undefined ? (
                // A ZERO TRIM is legal (`cut_trim_allowance_m >= 0`): there is
                // no waste piece at all, one physical separation, and the fault
                // sits on the join. There is nothing to move, and saying so
                // beats two controls that do nothing.
                <p className="floor-card-sub" data-testid="cut-handles-fixed">
                  {t("quality.cut.handlesFixed")}
                </p>
              ) : (
                <>
                  <p className="floor-card-sub">{t("quality.cut.handlesRule")}</p>
                  {/* THE HANDLES ARE STEPPERS, and that is a decision. §4.5 says
                      "two draggable cut handles"; a drag target on a 412 px
                      screen operated by a gloved fingertip (~14 mm) is not a
                      control this product ships — the floor's §3.10 rule is
                      ≥52 px targets and nothing that depends on precision
                      pointing. The CONSTRAINT §4.5 is actually about — outward
                      only, never inward past `fault ± trim` — is enforced here
                      and re-enforced by the server, and D-091's numpad is the
                      glove-usable way to key an exact metre mark. The diagram
                      above is the picture; these are the marks. */}
                  {/* SEEDED EXACT, SHOWN FORMATTED. The seed is the wire's own
                      `numeric(18,6)` text and it STAYS that text until the
                      operator types over it: `adjusted` compares it against
                      `waste.from_m`/`to_m` through `parseMeasure`, so a value
                      rounded or truncated for the sake of the display would
                      read as an adjustment nobody made and send a window the
                      server refuses as narrowed. `formatDisplay` is display
                      only — the same `formatMeasure` every other length on this
                      screen goes through, so the saw does not read
                      "49.500000" (or, in ru, "49,500000") in the one place the
                      operator is asked to key a number. */}
                  {/* AND EDITABLE BY CARET, WHICH `maxPrecision` IS FOR. The
                      seed is six-place wire text and the stepper's typed path
                      was fenced at the production wire's 3 dp, so the first
                      keystroke (which replaces the whole selection) worked and
                      every keystroke after a second tap — the tap that places
                      a caret — was refused: backspace on "1543.500250" yields
                      five fraction digits, the regex says no, and the field
                      looks broken. A handle's wire is module 06's
                      `numeric(18,6)`; saying so is the fix, and it is the only
                      call site in the app that says it. */}
                  <div className="floor-field">
                    <span className="floor-cut-handle-label">{t("quality.cut.handleA")}</span>
                    <QtyStepper
                      value={handleFrom}
                      onChange={setHandleFrom}
                      testId="cut-handle-a"
                      maxPrecision={MEASURE_MAX_PRECISION}
                      locale={i18n.language}
                      formatDisplay={handleDisplay}
                    />
                  </div>
                  <div className="floor-field">
                    <span className="floor-cut-handle-label">{t("quality.cut.handleB")}</span>
                    <QtyStepper
                      value={handleTo}
                      onChange={setHandleTo}
                      testId="cut-handle-b"
                      maxPrecision={MEASURE_MAX_PRECISION}
                      locale={i18n.language}
                      formatDisplay={handleDisplay}
                    />
                  </div>
                  {handleProblem !== undefined ? (
                    <p className="floor-cut-broken" role="alert" data-testid="cut-handle-problem">
                      {t(`quality.cut.handleProblem.${handleProblem}`)}
                    </p>
                  ) : null}
                </>
              )}
            </section>
          ) : null}

          {event.status === "confirmed" ? (
            <section className="floor-panel" data-testid="cut-summary">
              <h2 className="floor-panel-head">{t("quality.cut.summaryHead")}</h2>
              <p className="floor-card-sub">
                {t("quality.cut.summaryBody", {
                  count: segments.filter((s) => s.kind === "stock").length,
                })}
              </p>
            </section>
          ) : null}

          {event.status === "cancelled" ? (
            <FloorEmpty
              title={t("quality.cut.cancelledTitle")}
              body={event.cancelled_reason ?? t("quality.cut.cancelledBody")}
            />
          ) : null}

          {/* THE ACTIONS. Every one of them dead-with-its-reason offline. */}
          {event.status === "proposed" || event.status === "confirmed" ? (
            <section className="floor-cut-acts" data-testid="cut-acts">
              {event.status === "proposed" ? (
                <FloorButton
                  variant="primary"
                  testId="cut-confirm"
                  disabled={!arm.online || arm.busy || handleProblem !== undefined || !tiles}
                  disabledReason={
                    !arm.online
                      ? t("quality.cut.onlineOnly")
                      : handleProblem !== undefined
                        ? t(`quality.cut.handleProblem.${handleProblem}`)
                        : !tiles
                          ? t("quality.cut.tilingBlocked")
                          : undefined
                  }
                  onClick={() => {
                    void run(() =>
                      arm.confirm({
                        id: event.id,
                        // `adjusted` already carries "there IS a waste segment"
                        // (it compares against one), so the narrowing rides the
                        // alias — a second `waste !== undefined` here is a
                        // condition the type system knows can never be false.
                        adjustment: adjusted
                          ? {
                              seq: waste.seq,
                              from_m: normaliseHandle(handleFrom),
                              to_m: normaliseHandle(handleTo),
                            }
                          : undefined,
                      }),
                    );
                  }}
                >
                  {t("quality.cut.confirm")}
                </FloorButton>
              ) : null}

              {event.status === "confirmed" && !armed ? (
                <FloorButton
                  variant="danger"
                  testId="cut-execute-arm"
                  disabled={!arm.online || arm.busy || !tiles}
                  disabledReason={
                    !arm.online
                      ? t("quality.cut.onlineOnly")
                      : !tiles
                        ? t("quality.cut.tilingBlocked")
                        : undefined
                  }
                  onClick={() => {
                    setArmed(true);
                  }}
                >
                  {t("quality.cut.execute")}
                </FloorButton>
              ) : null}

              {/* THE DOUBLE-CONFIRM §4.5 asks for. Two taps, and the second one
                  says what it does rather than "OK": the drum is consumed. */}
              {event.status === "confirmed" && armed ? (
                <div className="floor-cut-arm" data-testid="cut-execute-armed">
                  <p className="floor-cut-arm-warn" role="alert">
                    {t("quality.cut.executeWarn")}
                  </p>
                  <FloorButton
                    variant="danger"
                    testId="cut-execute"
                    disabled={!arm.online || arm.busy}
                    disabledReason={!arm.online ? t("quality.cut.onlineOnly") : undefined}
                    onClick={() => {
                      void (async () => {
                        const outcome = await arm.execute(event.id);
                        if (outcome.kind === "done") {
                          setRefusal(undefined);
                          setExecuted(outcome.result);
                          setPlan({
                            cut_event: outcome.result.cut_event,
                            segments: outcome.result.segments,
                          });
                        } else {
                          setRefusal(outcome);
                        }
                        setArmed(false);
                        detail.refetch();
                      })();
                    }}
                  >
                    {t("quality.cut.executeConfirm")}
                  </FloorButton>
                  <FloorButton
                    variant="ghost"
                    testId="cut-execute-standdown"
                    onClick={() => {
                      setArmed(false);
                    }}
                  >
                    {t("quality.cut.executeStandDown")}
                  </FloorButton>
                </div>
              ) : null}

              {/* RE-PLAN. Always available on a live cut, and the honest
                  continuation of the length-changed 409: a new propose reads
                  module 06's current measured length and supersedes this one
                  in its own transaction (D-162), so the operator is never
                  stranded by a plan they cannot see. */}
              <FloorButton
                testId="cut-replan"
                disabled={!arm.online || arm.busy}
                disabledReason={!arm.online ? t("quality.cut.onlineOnly") : undefined}
                onClick={() => {
                  setArmed(false);
                  void run(() => arm.propose(id));
                }}
              >
                {t("quality.cut.replan")}
              </FloorButton>

              {cancelOpen ? (
                <div className="floor-field" data-testid="cut-cancel-form">
                  <label htmlFor="cut-cancel-reason">{t("quality.cut.cancelReason")}</label>
                  <input
                    id="cut-cancel-reason"
                    type="text"
                    autoComplete="off"
                    value={cancelReason}
                    data-testid="cut-cancel-reason"
                    onChange={(e) => {
                      setCancelReason(e.target.value);
                    }}
                  />
                  <FloorButton
                    variant="danger"
                    testId="cut-cancel-confirm"
                    disabled={!arm.online || arm.busy || cancelReason.trim().length === 0}
                    disabledReason={
                      !arm.online
                        ? t("quality.cut.onlineOnly")
                        : cancelReason.trim().length === 0
                          ? t("quality.cut.cancelReasonRequired")
                          : undefined
                    }
                    onClick={() => {
                      void (async () => {
                        await run(() => arm.cancel({ id: event.id, reason: cancelReason.trim() }));
                        setCancelOpen(false);
                        setCancelReason("");
                      })();
                    }}
                  >
                    {t("quality.cut.cancelConfirm")}
                  </FloorButton>
                </div>
              ) : (
                <FloorButton
                  variant="ghost"
                  testId="cut-cancel-open"
                  onClick={() => {
                    setCancelOpen(true);
                  }}
                >
                  {t("quality.cut.cancel")}
                </FloorButton>
              )}
            </section>
          ) : null}
        </>
      ) : null}

      {/* DONE. The refs the server minted, said plainly: two quarantined drums
          and two retests waiting, which is the state §3.5d step 4 hands the
          floor next. `waste_event_id` is deliberately not here (D-157) — module
          05 mints it after the commit and no synchronous response can carry it. */}
      {executed !== undefined ? (
        <section className="floor-panel" data-testid="cut-done">
          <h2 className="floor-panel-head">{t("quality.cut.doneTitle")}</h2>
          <p className="floor-card-sub">
            {t("quality.cut.doneUnits", { count: executed.new_stock_unit_ids.length })}
          </p>
          <p className="floor-card-sub">
            {t("quality.cut.doneRetests", { count: executed.retest_inspection_ids.length })}
          </p>
          <Link to="/floor/quality" className="floor-go-lead" data-testid="cut-done-queue">
            <span className="floor-card-title">{t("quality.cut.doneToQueue")}</span>
          </Link>
        </section>
      ) : null}
    </FloorScreen>
  );
}

/** A handle value typed on the stepper is 3-dp canonical text; the wire wants
 * module 06's 6-dp scale. Converted through the scaled integer, never through
 * `Number` — and echoed verbatim when it will not parse, so the SERVER refuses
 * it with its own sentence rather than this function inventing a length. */
function normaliseHandle(text: string): string {
  const scaled = parseMeasure(text);
  return scaled === undefined ? text : measureToText(scaled);
}

/**
 * §4.5's horizontal cable diagram: the full length to scale, the fault marker,
 * and the segments in their kinds.
 *
 * `aria-hidden`, and that is not a shortcut: every fact it draws is also in the
 * segment list beside it, in words and with exact numbers, so a screen reader
 * gets the plan rather than a description of a rectangle. Percentages come from
 * `positionPct`, the one float in this surface.
 */
function CutDiagram({
  segments,
  sourceLengthM,
  faultPositionM,
}: {
  readonly segments: readonly QualityCutSegment[];
  readonly sourceLengthM: string;
  readonly faultPositionM: string;
}) {
  const faultPct = positionPct(faultPositionM, sourceLengthM);
  return (
    <div className="floor-cut-diagram" data-testid="cut-diagram" aria-hidden="true">
      <div className="floor-cut-cable">
        {segments.map((segment) => {
          const from = positionPct(segment.from_m, sourceLengthM);
          const to = positionPct(segment.to_m, sourceLengthM);
          if (from === undefined || to === undefined) return null;
          return (
            <span
              key={segment.id}
              className={
                segment.kind === "stock"
                  ? "floor-cut-piece floor-cut-piece-stock"
                  : "floor-cut-piece floor-cut-piece-waste"
              }
              style={{ left: `${String(from)}%`, width: `${String(to - from)}%` }}
            />
          );
        })}
        {faultPct !== undefined ? (
          <span className="floor-cut-fault" style={{ left: `${String(faultPct)}%` }} />
        ) : null}
      </div>
    </div>
  );
}
